import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Download, Calendar, Building, TrendingUp, DollarSign, Package, BarChart3 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { usePageFocus } from '@/hooks/usePageFocus';
import { triggerReloadSearch, pickReloadSearch, triggerReloadSearchWithDate } from '@/utils/reloadSearch';
import { companyService } from '@/services/companyService';

// 模組級別快取狀態，避免重掛載時重新載入數據
let cachedOverviewState: DocumentOverviewState | null = null;

// 數據介面定義
interface DocumentOverviewData {
  // 基本資訊
  companyName: string;
  searchDate: string;

  // 銷售統計
  orderCount: number;      // 單數
  itemCount: number;       // 件數
  dailyGMV: number;        // 單日GMV
  monthlyGMC: number;      // 總GMC

  // 財務指標
  dailyProfit: number;     // 單日利潤
  monthlyProfit: number;   // 總利潤
  dailyGrossMarginRate: number;  // 單日毛利率
  totalInventoryValue: number;    // 總庫存價值
  dailyProfitRate: number;        // 單日利潤率
}

// 公司資訊介面
interface CompanyInfo {
  id: number;
  name: string;
  code?: string;
}

// 狀態介面
interface DocumentOverviewState {
  data: DocumentOverviewData | null;
  loading: boolean;
  error: string | null;
  selectedDate: string;
  currentCompany: CompanyInfo | null;
}

const ORDER_CATEGORY: CategoryKey = 'order';

const DocumentOverview: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  // 組件狀態
  const [state, setState] = useState<DocumentOverviewState>(() => {
    // 如果有快取數據，使用快取數據並設置 loading 為 false
    if (cachedOverviewState?.data) {
      return {
        ...cachedOverviewState,
        loading: false, // 確保從快取恢復時不會顯示載入狀態
      };
    }
    // 否則使用預設狀態
    return {
      data: null,
      loading: false,
      error: null,
      selectedDate: new Date().toISOString().split('T')[0], // 預設今天
      currentCompany: null,
    };
  });

  // 組件掛載標記（用於組件重掛載保護）
  const isMountedRef = useRef(true);

  // 跟踪加载开始时间，用于检测stale loading状态
  const loadingStartedAtRef = useRef<number | null>(null);

  // 用於清理函數的狀態快照（避免依賴整個 state）
  const stateSnapshotRef = useRef<DocumentOverviewState | null>(null);

  // 暫存待回放的日期選擇，確保公司資訊就緒後仍能執行
  const pendingDateReloadRef = useRef<string | null>(null);

  // 跟踪stale loading恢复定时器
  const staleRecoveryTimerRef = useRef<number | null>(null);

  // 页面焦点恢复处理器
  const handlePageFocusRecovery = useCallback(async () => {
    const now = Date.now();
    const STALE_LOADING_THRESHOLD = 3000; // 3秒

    console.log('🔄 DocumentOverview頁面焦點恢復');

    // 捕获当前状态到本地变量
    const savedDate = state.selectedDate;
    const savedCompany = state.currentCompany;

    // 检查是否是stale loading状态
    if (state.loading && loadingStartedAtRef.current) {
      const loadingDuration = now - loadingStartedAtRef.current;
      if (loadingDuration > STALE_LOADING_THRESHOLD) {
        console.log(`🔄 檢測到stale loading狀態 (${loadingDuration}ms)，重置loading狀態並重新載入`);
        setState(prev => ({ ...prev, loading: false }));
        loadingStartedAtRef.current = null;

        // 立即重新载入数据
        try {
          if (savedCompany && savedDate) {
            await loadDocumentOverview(savedDate, savedCompany.id);
          }
        } catch (error) {
          console.error('DocumentOverview stale loading恢復失敗:', error);
        }
        return;
      } else {
        // 仍在正常的加载时间内，跳过恢复
        console.log(`🔄 DocumentOverview正在正常載入中 (${loadingDuration}ms)，跳過恢復`);
        return;
      }
    }

    // 如果有日期和公司信息，尝试重新加载数据以确保数据最新
    if (savedDate && savedCompany && !state.loading) {
      console.log('🔄 DocumentOverview檢查數據是否需要重新載入');

      // 检查是否需要刷新数据（比如页面离开太久）
      const timeSinceLastLoad = loadingStartedAtRef.current ?
        now - loadingStartedAtRef.current : 0;

      // 如果距离上次加载超过30秒，或者没有数据，重新加载
      if (timeSinceLastLoad > 30000 || !state.data) {
        console.log(`🔄 DocumentOverview重新載入數據 (${timeSinceLastLoad}ms since last load)`);
        try {
          await loadDocumentOverview(savedDate, savedCompany.id);
        } catch (error) {
          console.error('DocumentOverview數據重新載入失敗:', error);
        }
      } else {
        console.log('🔄 DocumentOverview數據仍然新鮮，跳過重新載入');
      }
    }
  }, [state.loading, state.data, state.selectedDate, state.currentCompany]);

  // 使用頁面焦點恢復 hook - 禁用自動監聽，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 1000,
    enabled: false
  });

  // 初始化公司資訊
  useEffect(() => {
    const initializeCompany = async () => {
      try {
        await ensureAuthReady();

        // 獲取當前用戶
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/login');
          return;
        }

        // 優先檢查localStorage中的公司選擇
        const selectedCompanyId = localStorage.getItem('selectedCompanyId');
        let companyInfo: CompanyInfo | null = null;

        if (selectedCompanyId) {
          // 如果有選擇的公司，從資料庫獲取完整信息
          try {
            const companyData = await companyService.getCompanyById(selectedCompanyId);
            if (companyData) {
              companyInfo = {
                id: Number(companyData.id),
                name: companyData.name,
                code: companyData.code
              };
              console.log('✅ 從localStorage獲取的公司信息:', companyInfo);
            }
          } catch (error) {
            console.warn('從localStorage獲取公司信息失敗:', error);
          }
        }

        // 如果沒有從localStorage獲取到，嘗試使用companyService
        if (!companyInfo) {
          const currentCompanyData = await companyService.getCurrentCompany();
          if (currentCompanyData) {
            companyInfo = {
              id: Number(currentCompanyData.id),
              name: currentCompanyData.name,
              code: currentCompanyData.code
            };
            console.log('✅ 從companyService獲取的公司信息:', companyInfo);
          }
        }

        // 如果還是沒有公司信息，顯示警告
        if (!companyInfo) {
          console.warn('❌ 用戶沒有選擇公司，無法載入數據');
          toast.warning(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
          setState(prev => ({ ...prev, loading: false }));
          return;
        }

        // 檢查是否有快取數據且公司匹配
        if (cachedOverviewState?.data && cachedOverviewState.currentCompany?.id === companyInfo.id) {
          console.log('📋 使用快取數據，跳過重新載入');
          // 從快取恢復公司信息和數據
          setState(prev => ({
            ...prev,
            currentCompany: cachedOverviewState.currentCompany,
            data: cachedOverviewState.data,
            selectedDate: cachedOverviewState.selectedDate,
            loading: false,
            error: cachedOverviewState.error
          }));
          return;
        }

        // 設置公司信息（不自動載入數據，依靠快取）
        setState(prev => ({
          ...prev,
          currentCompany: companyInfo
        }));

        console.log(`📊 已設置公司 ${companyInfo.name} (ID: ${companyInfo.id})，等待用戶手動刷新`);

      } catch (error) {
        console.error('❌ 初始化公司資訊失敗:', error);
        toast.error(language === 'zh' ? '獲取公司資訊失敗，請重新整理頁面' : 'Failed to get company information, please refresh the page');
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    initializeCompany();

    // 組件卸載時清理並持久化狀態到快取
    return () => {
      isMountedRef.current = false;
      // 使用狀態快照來持久化到快取，避免重新掛載時丟失數據
      if (stateSnapshotRef.current) {
        cachedOverviewState = {
          data: stateSnapshotRef.current.data,
          loading: false, // 卸載時不應該是載入狀態
          error: stateSnapshotRef.current.error,
          selectedDate: stateSnapshotRef.current.selectedDate,
          currentCompany: stateSnapshotRef.current.currentCompany
        };
        console.log('💾 組件卸載時保存狀態到快取:', cachedOverviewState);
      }
    };
  }, [navigate, language]);

  // 同步狀態快照，避免主要 effect 依賴整個 state
  useEffect(() => {
    stateSnapshotRef.current = state;
  }, [state]);

  // 首次載入時讀取待回放的日期選擇並保存
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending || !pending.filters?.selectedDate || pending.filters?.source !== 'date_selection') return;

    pendingDateReloadRef.current = pending.filters.selectedDate;

    // 立即保存待回放的日期到 UI 狀態，避免遺失
    console.log('🔄 保存待回放日期選擇到 UI 狀態:', pending.filters.selectedDate);
    setState(prev => ({ ...prev, selectedDate: pending.filters.selectedDate }));
  }, []);

  // 公司資訊就緒後回放日期選擇
  useEffect(() => {
    if (!state.currentCompany?.id) {
      console.log('🔄 等待公司資訊就緒...');
      return;
    }

    const pendingDate = pendingDateReloadRef.current;
    if (!pendingDate) return;

    const executePendingDateSelection = async () => {
      console.log('🔄 檢測到待回放的日期選擇，開始執行');
      try {
        // 保險：確保 Auth/Session ready 再載入
        await ensureAuthReady();
        await loadDocumentOverview(pendingDate, Number(state.currentCompany.id));
        toast.success(language === 'zh' ? '日期選擇已恢復並載入數據' : 'Date selection restored and data loaded');
      } catch (error) {
        console.error('回放日期選擇失敗:', error);
        toast.error(language === 'zh' ? '日期選擇恢復失敗，請重新選擇日期' : 'Date selection restore failed, please select date again');
      } finally {
        pendingDateReloadRef.current = null;
      }
    };

    executePendingDateSelection();
  }, [state.currentCompany, language]); // 移除 loadDocumentOverview 依賴避免無限循環

  // 載入單據總覽數據
  const loadDocumentOverview = useCallback(async (date: string, companyId: number) => {
    if (!isMountedRef.current) return;

    setState(prev => ({ ...prev, loading: true, error: null }));
    loadingStartedAtRef.current = Date.now(); // 记录加载开始时间

    try {
      console.log(`📊 載入單據總覽數據: 日期=${date}, 公司ID=${companyId}`);

      // 1. 當日銷售統計查詢
      console.log(`🔍 查詢當日銷售統計: 公司ID=${companyId}, 日期=${date}`);
      const { data: dailyStats, error: dailyError } = await supabase
        .from('sales_orders')
        .select(`
          order_id,
          sale_quantity,
          total_amount,
          snapshot_gross_profit
        `)
        .eq('company_id', companyId)
        .gte('order_date', date)
        .lt('order_date', new Date(new Date(date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

      if (dailyError) {
        console.error('❌ 當日統計查詢失敗:', dailyError);
        throw new Error('Failed to load daily statistics');
      }
      console.log(`✅ 當日統計查詢成功: 找到 ${dailyStats?.length || 0} 筆記錄`);

      // 2. 月度銷售統計查詢
      const monthStart = new Date(date);
      monthStart.setDate(1);
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);

      console.log(`🔍 查詢月度銷售統計: 公司ID=${companyId}, 月份=${monthStart.toISOString().split('T')[0]} 到 ${monthEnd.toISOString().split('T')[0]}`);
      const { data: monthlyStats, error: monthlyError } = await supabase
        .from('sales_orders')
        .select('total_amount, snapshot_gross_profit')
        .eq('company_id', companyId)
        .gte('order_date', monthStart.toISOString().split('T')[0])
        .lt('order_date', monthEnd.toISOString().split('T')[0]);

      if (monthlyError) {
        console.error('❌ 月度統計查詢失敗:', monthlyError);
        throw new Error('Failed to load monthly statistics');
      }
      console.log(`✅ 月度統計查詢成功: 找到 ${monthlyStats?.length || 0} 筆記錄`);

      // 3. 總庫存價值：透過 RPC 在資料庫端直接計算 SUM(qty * cost)
      //    前端只接收 1 個數字，避免傳輸大量明細資料及 Supabase JS 1000 筆上限問題
      console.log(`🔍 查詢庫存價值 (RPC): 公司ID=${companyId}`);
      const { data: inventoryValueRpc, error: inventoryValueError } = await supabase
        .rpc('get_inventory_value', { p_company_id: companyId });

      if (inventoryValueError) {
        console.error('❌ 庫存價值 RPC 查詢失敗:', inventoryValueError);
      } else {
        console.log(`✅ 庫存價值 RPC 查詢成功: ${inventoryValueRpc}`);
      }

      const totalInventoryValue = Number(inventoryValueRpc) || 0;

      // 4. 計算數據
      const orderCount = dailyStats ? new Set(dailyStats.map(item => item.order_id)).size : 0;
      const itemCount = dailyStats ? dailyStats.reduce((sum, item) => sum + (item.sale_quantity || 0), 0) : 0;
      const dailyGMV = dailyStats ? dailyStats.reduce((sum, item) => sum + (item.total_amount || 0), 0) : 0;
      const dailyProfit = dailyStats ? dailyStats.reduce((sum, item) => sum + (item.snapshot_gross_profit || 0), 0) : 0;

      const monthlyGMC = monthlyStats ? monthlyStats.reduce((sum, item) => sum + (item.total_amount || 0), 0) : 0;
      const monthlyProfit = monthlyStats ? monthlyStats.reduce((sum, item) => sum + (item.snapshot_gross_profit || 0), 0) : 0;

      // 毛利率計算
      const dailyGrossMarginRate = dailyGMV > 0 ? (dailyProfit / dailyGMV) * 100 : 0;

      // 單日利潤率計算 (單日利潤 / 現庫存價值)
      const dailyProfitRate = totalInventoryValue > 0 ? (dailyProfit / totalInventoryValue) * 100 : 0;

      const overviewData: DocumentOverviewData = {
        companyName: state.currentCompany?.name || '未知公司',
        searchDate: date,
        orderCount,
        itemCount,
        dailyGMV,
        monthlyGMC,
        dailyProfit,
        monthlyProfit,
        dailyGrossMarginRate,
        totalInventoryValue,
        dailyProfitRate
      };

      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          data: overviewData,
          loading: false
        }));

        loadingStartedAtRef.current = null; // 清除加载开始时间

        // 清除stale recovery定时器
        if (staleRecoveryTimerRef.current) {
          clearTimeout(staleRecoveryTimerRef.current);
          staleRecoveryTimerRef.current = null;
        }

        console.log('✅ 單據總覽數據載入完成:', overviewData);
        console.log('📊 總庫存價值:', totalInventoryValue);
        console.log('📊 單日利潤率:', dailyProfitRate);
        console.log('📊 月度利潤:', monthlyProfit);
        console.log('📊 當日利潤:', dailyProfit);
        console.log('📊 當日統計:', { orderCount, itemCount, dailyGMV, dailyProfit });
        console.log('📊 月度統計:', { monthlyGMC, monthlyProfit });
        console.log('📊 庫存價值來源: RPC get_inventory_value');

        // 更新快取狀態
        cachedOverviewState = {
          data: overviewData,
          loading: false,
          error: null,
          selectedDate: date,
          currentCompany: state.currentCompany
        };
      }

    } catch (error) {
      console.error('載入單據總覽數據失敗:', error);

      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
      }

      loadingStartedAtRef.current = null; // 清除加载开始时间

      // 清除stale recovery定时器
      if (staleRecoveryTimerRef.current) {
        clearTimeout(staleRecoveryTimerRef.current);
        staleRecoveryTimerRef.current = null;
      }

      toast.error(language === 'zh' ? '載入數據失敗' : 'Failed to load data');

      // 即使出錯也更新快取狀態（保留之前的數據，但記錄錯誤）
      cachedOverviewState = {
        data: state.data, // 保留之前的數據
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        selectedDate: state.selectedDate,
        currentCompany: state.currentCompany
      };
    }
  }, [state.currentCompany, language]);

  // 處理日期選擇
  const handleDateSelect = useCallback((date: string) => {
    // 先更新UI狀態，保留用戶選擇的日期
    setState(prev => ({ ...prev, selectedDate: date }));

    if (state.currentCompany) {
      console.log(`📅 用戶選擇日期: ${date}, 使用公司 ${state.currentCompany.name} (ID: ${state.currentCompany.id})`);

      // 🔄 使用專業的重新連接機制，保留選擇的日期並在背後實現重新連接
      console.log('🔄 觸發專業重新連接機制，日期:', date);
      triggerReloadSearchWithDate(date);
    } else {
      console.warn('❌ 無法載入數據：沒有公司信息');
      toast.warning(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
    }
  }, [state.currentCompany, language]);

  // 手動刷新處理器（用於刷新按鈕等）
  const handleManualRefresh = useCallback(() => {
    if (state.currentCompany) {
      console.log(`🔄 用戶手動刷新，使用公司 ${state.currentCompany.name} (ID: ${state.currentCompany.id}) 和日期 ${state.selectedDate}`);
      loadDocumentOverview(state.selectedDate, state.currentCompany.id);
    } else {
      console.warn('❌ 無法刷新：沒有公司信息');
      toast.warning(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
    }
  }, [state.currentCompany, state.selectedDate, loadDocumentOverview, language]);

  // 處理匯出
  const handleExport = useCallback(async () => {
    if (!state.data) {
      toast.error(language === 'zh' ? '沒有數據可匯出' : 'No data to export');
      return;
    }

    try {
      // 這裡實現Excel匯出邏輯
      toast.info(language === 'zh' ? '匯出功能開發中' : 'Export feature in development');
    } catch (error) {
      console.error('匯出失敗:', error);
      toast.error(language === 'zh' ? '匯出失敗' : 'Export failed');
    }
  }, [state.data, language]);

  // 格式化貨幣顯示
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('zh-HK', {
      style: 'currency',
      currency: 'HKD'
    }).format(amount);
  };

  // 格式化百分比顯示
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* 頁面標題 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              localStorage.setItem('selectedCategory', ORDER_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="text-muted-foreground hover:text-foreground"
          >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'zh' ? '返回' : 'Back'}
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                {language === 'zh' ? '單據總覽' : 'Document Overview'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {language === 'zh' ? '當日銷售數據統計與分析' : 'Daily sales data statistics and analysis'}
              </p>
            </div>
          </div>

          {/* 匯出按鈕 */}
          <Button
            onClick={handleExport}
            disabled={!state.data || state.loading}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            {language === 'zh' ? '匯出報表' : 'Export Report'}
          </Button>
        </div>

        {/* 日期選擇器 */}
        <Card className="bg-card/80 backdrop-blur-lg border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Calendar className="w-5 h-5" />
              {language === 'zh' ? '選擇日期' : 'Select Date'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="search-date" className="text-foreground">
                  {language === 'zh' ? '搜尋日期' : 'Search Date'}
                </Label>
                <Input
                  id="search-date"
                  type="date"
                  value={state.selectedDate}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  className="bg-background/50 border-input text-foreground"
                  disabled={state.loading}
                />
              </div>
              <div className="text-sm text-muted-foreground">
                {state.currentCompany && (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    {state.currentCompany.name}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 載入狀態 */}
        {state.loading && (
          <Card className="bg-card/80 backdrop-blur-lg border-border">
            <CardContent className="p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                <span className="ml-2 text-foreground">
                  {language === 'zh' ? '載入數據中...' : 'Loading data...'}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 錯誤狀態 */}
        {state.error && (
          <Card className="bg-destructive/20 border-destructive">
            <CardContent className="p-4">
              <div className="text-destructive-foreground">
                {language === 'zh' ? '載入失敗' : 'Load Failed'}: {state.error}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 數據展示區域 */}
        {state.data && !state.loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* 基本資訊卡片 */}
            <Card className="bg-card/80 backdrop-blur-lg border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-foreground">
                  <Building className="w-5 h-5" />
                  {language === 'zh' ? '基本資訊' : 'Basic Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '公司名稱' : 'Company Name'}
                  </div>
                  <div className="text-lg font-semibold text-foreground">
                    {state.data.companyName}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '搜尋日期' : 'Search Date'}
                  </div>
                  <div className="text-lg font-semibold text-foreground">
                    {new Date(state.data.searchDate).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US')}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 銷售統計卡片 */}
            <Card className="bg-card/80 backdrop-blur-lg border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-foreground">
                  <Package className="w-5 h-5" />
                  {language === 'zh' ? '銷售統計' : 'Sales Statistics'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '單數/件數' : 'Orders/Items'}
                  </div>
                  <div className="text-xl font-bold text-green-400">
                    {language === 'zh'
                      ? `${state.data.orderCount}單數 / ${state.data.itemCount}件`
                      : `${state.data.orderCount} Order / ${state.data.itemCount} item`}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '單日GMV' : 'Daily GMV'}
                  </div>
                  <div className="text-xl font-bold text-blue-400">
                    {formatCurrency(state.data.dailyGMV)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '總GMC' : 'Monthly GMC'}
                  </div>
                  <div className="text-lg font-semibold text-cyan-400">
                    {formatCurrency(state.data.monthlyGMC)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 財務指標卡片 */}
            <Card className="bg-card/80 backdrop-blur-lg border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-foreground">
                  <TrendingUp className="w-5 h-5" />
                  {language === 'zh' ? '財務指標' : 'Financial Metrics'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '單日利潤' : 'Daily Profit'}
                  </div>
                  <div className="text-xl font-bold text-green-400">
                    {formatCurrency(state.data.dailyProfit)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '總利潤' : 'Monthly Profit'}
                  </div>
                  <div className="text-lg font-semibold text-cyan-400">
                    {formatCurrency(state.data.monthlyProfit)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'zh' ? '單日毛利率' : 'Daily Gross Margin'}
                  </div>
                  <div className="text-lg font-semibold text-yellow-400">
                    {formatPercentage(state.data.dailyGrossMarginRate)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 庫存價值卡片 */}
            <Card className="bg-card/80 backdrop-blur-lg border-border md:col-span-2 lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-foreground">
                  <DollarSign className="w-5 h-5" />
                  {language === 'zh' ? '庫存價值分析' : 'Inventory Value Analysis'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-white mb-2">
                      {language === 'zh' ? '總庫存價值' : 'Total Inventory Value'}
                    </div>
                    <div className="text-2xl font-bold text-purple-400">
                      {state.data.totalInventoryValue !== undefined && state.data.totalInventoryValue !== null
                        ? formatCurrency(state.data.totalInventoryValue)
                        : '$0.00'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-white mb-2">
                      {language === 'zh' ? '單日利潤/現庫存價值' : 'Daily Profit/Current Inventory'}
                    </div>
                    <div className="text-2xl font-bold text-orange-400">
                      {state.data.dailyProfitRate !== undefined && state.data.dailyProfitRate !== null
                        ? formatPercentage(state.data.dailyProfitRate)
                        : '0.00%'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        )}

      </div>
    </div>
  );
};

export default DocumentOverview;
